import { COUNTRIES } from '@revolugo/common/countries'
import { isNil } from '@revolugo/common/utils'
import { omitBy } from 'lodash-es'

import type { PrebookFormValues } from '@revolugo/elements-vue3'
import type { LocationQueryValue } from 'vue-router'

function parseNationality(
  nationality: LocationQueryValue | LocationQueryValue[] | null,
): string | undefined {
  if (!nationality) {
    return undefined
  }

  const parsedNationality = (
    Array.isArray(nationality) ? nationality[0] : nationality
  )?.toLowerCase()

  const countries = Object.values(COUNTRIES)

  const iso2Match = countries.find(
    country => country.iso2.toLowerCase() === parsedNationality,
  )

  if (iso2Match) {
    return iso2Match.iso2
  }

  const nameMatch = countries.find(
    country =>
      country.name.toLowerCase() === parsedNationality ||
      country.name.split('(')[0]?.trim()?.toLowerCase() === parsedNationality,
  )

  if (nameMatch) {
    return nameMatch.iso2
  }

  return undefined
}

export const usePrebookQuery = (): {
  prebookProps: ComputedRef<Partial<PrebookFormValues>>
  updatePrebookQuery: (payload: PrebookFormValues) => Promise<void>
} => {
  const router = useRouter()
  const { query } = toRefs(useRoute())

  const parsedNationality = parseNationality(
    query.value.nationality || query.value.cNationality,
  )
  const parsedPhoneCountry = parseNationality(
    query.value.phoneCountry || query.value.cPhoneCountry,
  )

  const prebookProps = computed(() =>
    omitBy(
      {
        emailAddress: query.value.email || query.value.cEmail,
        firstName:
          query.value.firstName ||
          query.value.firstname ||
          query.value.cFirstname,
        lastName:
          query.value.lastName || query.value.lastname || query.value.cLastname,
        nationality: parsedNationality,
        organizationAddress:
          query.value.organizationAddress || query.value.oAddress,
        organizationCity: query.value.organizationCity || query.value.oCity,
        organizationCountry:
          query.value.organizationCountry || query.value.oCountry,
        organizationName: query.value.organizationName || query.value.oName,
        organizationState: query.value.organizationState || query.value.oState,
        organizationVatNumber:
          query.value.organizationVatNumber || query.value.oVat,
        organizationZipCode:
          query.value.organizationZipCode ||
          query.value.oZip ||
          query.value.oZipcode,
        phoneCountry: parsedPhoneCountry || parsedNationality,
        phoneNumber: query.value.phoneNumber || query.value.cPhone,
        salutation:
          query.value.salutation ||
          query.value.title ||
          query.value.cSalutation,
      },
      isNil,
    ),
  )

  async function updatePrebookQuery(payload: PrebookFormValues) {
    await router.replace({
      query: omitBy(
        {
          ...query.value,

          email: payload.emailAddress,
          firstName: payload.firstName,
          lastName: payload.lastName,
          nationality: payload.nationality,
          phoneCountry: payload.phoneCountry,
          phoneNumber: payload.phoneNumber,
          salutation: payload.salutation,
        },
        isNil,
      ) as Record<string, string>,
    })
  }

  return {
    prebookProps,
    updatePrebookQuery,
  }
}
